import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { emailMaxLengthValidator, ValidationPatterns } from "@bbwt/modules/validation";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { createGenderOptions, createUserTitleOptions, PatientLogin, UserService } from "@main/users";
import { FormButtonsConfig } from "@mipro/components";
import { DuplicatePatient, DuplicatePatientCheckResult } from "./duplicate-patient";
import { PlayerService } from "./player.service";
import { EpisodeOfflineStorage } from "@project/episode/episode-offline.storage";
import { PrescriptionLineOfflineStorage } from "@project/prescription-shared/classes/prescription-line-offline.storage";
import { WalkInOfflineStorage } from "@project/walk-ins/classes/walk-in-offline.storage";
import { OrganizationService } from "@main/companies";
import { UserHelperService } from "@main/users/user-helper.service";
import { MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";

@Component({
    selector: "create-player-form",
    templateUrl: "./create-player-form.component.html",
    styles: []
})
export class CreatePlayerFormComponent implements OnInit, AfterViewInit {
    playerForm = this.fb.group({
        firstName: [null, [Validators.required, Validators.maxLength(35)]],
        lastName: [null, [Validators.required, Validators.maxLength(35)]],
        dateOfBirth: [null, Validators.required],
        gender: [null, Validators.required],
        phoneNumber: [null, Validators.pattern(ValidationPatterns.phone)],
        email: [null, [Validators.pattern(ValidationPatterns.email), emailMaxLengthValidator]],
        organizationGroups: [[], Validators.required],
        consentToUpdate: [false],
        title: [null, Validators.required],
        identificationType: [null]
    });
    creatingPlayer = false;
    buttonsConfig = <FormButtonsConfig>{
        cancelFunc: () => this.cancel.emit()
    };
    genders = createGenderOptions();
    duplicatesDialogVisible = false;
    duplicatePatients: DuplicatePatientCheckResult;
    titleOptions = createUserTitleOptions();

    identityVerificationOptions = [
        { label: "Passport", value: "Passport" },
        { label: "Driver's license", value: "Driver's license" },
        { label: "ID card", value: "ID card" },
        { label: "Personal vouching", value: "Personal vouching" },
        { label: "Others", value: "Others" }
    ];
    isIdentityVerificationMandatory: boolean = false;
    orgIdSelected: string[];

    @Input() data: PatientLogin;

    @Input()
    inputsGroupStyle: any;

    @Output() readonly playerCreated = new EventEmitter<PatientLogin | DuplicatePatient>();
    @Output() readonly cancel = new EventEmitter();
    @Output() readonly emailNotSent = new EventEmitter();

    constructor(
        private fb: UntypedFormBuilder,
        private playerService: PlayerService,
        private patientService: PatientService,
        private userHelperService: UserHelperService,
        private organizationService: OrganizationService,
        private messageService: MessageService
    ) {}

    async ngOnInit() {
        const hashedOrganizationId = this.userHelperService.hashedOrganizationId;
        const orgIds = Array.isArray(hashedOrganizationId)
            ? hashedOrganizationId.map(id => parseInt(id.split("-")[0], 10))
            : [parseInt(hashedOrganizationId.split("-")[0], 10)];

        if(orgIds.length > 0){
            this.isIdentityVerificationMandatory = await this.organizationService.isMandatoryIdentityVerificationEnabled(orgIds);
        } else {
            this.isIdentityVerificationMandatory = false;
        }
    }

    getOrgId(data: string[]) {
        this.orgIdSelected = [...data];

        setTimeout(() => {
            this.updateIdentityVerificationRequirement(this.orgIdSelected);
        }, 0);
    }

    get organizationGroups(): string[]{
        return this.playerForm.get("organizationGroups")?.value;
    }

    ngAfterViewInit() {
        if (this.data) {
            this.playerForm.patchValue({
                firstName: this.data.firstName,
                lastName: this.data.lastName,
                email: this.data.email,
                dateOfBirth: new Date(this.data.dateOfBirth.toString()),
                phoneNumber: this.data.phoneNumber,
                gender: this.data.gender,
                organizationGroups: this.data.organizationGroups,
                title: this.data.title,
                consentToUpdate: this.data.consentToUpdate,
                identificationType: null
            });
        }
    }

    async updateIdentityVerificationRequirement(orgIds?: string[]) {
        if(orgIds?.length > 0){
            const extractedOrgIds = (orgIds || [])
            .map(orgId => parseInt(orgId.split("-")[0], 10))
            .filter(id => !isNaN(id));

            this.organizationService.isMandatoryIdentityVerificationEnabled(extractedOrgIds)
            .then(response => {
                this.isIdentityVerificationMandatory = response;
            })
            .catch(error => {
                this.isIdentityVerificationMandatory = false;
            });
        } else {
            this.isIdentityVerificationMandatory = false;
        }

        const identityVerificationControl = this.playerForm.get("identificationType");
        if (this.isIdentityVerificationMandatory) {
            identityVerificationControl.setValidators(Validators.required);
        } else {
            identityVerificationControl.clearValidators();
        }
        identityVerificationControl.updateValueAndValidity(); // Ensure validation is recalculated
    }

    async createPlayer() {
        if (this.playerForm.invalid) {
            this.playerForm.markAllAsTouched();
            return;
        }

        this.creatingPlayer = true;
        const player = { ...this.playerForm.value, ...{ id: this.data?.id } };
        // fix 324015. If patient-consent is empty. First time consentToUpdate=false but when user rser try to create second consentToUpdate=null.
        // for creating user this field cannot be null. 
        player.consentToUpdate = player.consentToUpdate || false;
        try {
            const patientOrDuplicates = await this.playerService.create(player);
            if (
                Array.isArray((<DuplicatePatientCheckResult>patientOrDuplicates).matches) ||
                Array.isArray((<DuplicatePatientCheckResult>patientOrDuplicates).fuzzyNameMatches)
            ) {
                this.duplicatePatients = <DuplicatePatientCheckResult>patientOrDuplicates;
                this.duplicatesDialogVisible = true;
            } else {
                this.emitPatient(<PatientLogin>patientOrDuplicates);
            }
        } catch (error) {
            if (error.error?.includes("Email rejected")) {
                this.emailNotSent.emit();
                this.emitPatient(player);
            } else if (error.error?.match(/^Email already in use, please update and try again$/)) {
                this.playerForm.get("email").setErrors({ emailInUse: true });
            } else if (error.error?.includes("Patient with the same Email already exists in this Organisation.")) {
                this.messageService.add(Message.Warning(error.error));
            }
        } finally {
            this.creatingPlayer = false;
        }
    }

    emitPatient(patient: PatientLogin) {
        if (patient) {
            const patientId = patient.patientId;
            this.patientService.triggerPatientsUpdated(patientId);
            if (this.data) {
                WalkInOfflineStorage.updatePatientId(this.data.id, patientId);
                EpisodeOfflineStorage.updatePatientId(this.data.id, patientId);
                PrescriptionLineOfflineStorage.updatePatientId(this.data.id, patientId);
                // Update every offline element depending on this patient
            }
        }
        this.playerCreated.emit(patient);
    }
}
